import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { SectionHeader, EmptyState } from "../components/UI";
import { TID } from "../constants/testIds";
import { toast } from "sonner";
import { Plus, LifeBuoy, Search, X, Paperclip, Trash2 } from "lucide-react";

const CATEGORIES = ["General", "Billing", "Technical Issue", "Feature Request", "Account", "Security", "Other"];
const PRIORITIES = ["low", "medium", "high", "critical"];
const MAX_TICKET_FILE_MB = 25;

const STATUS_STYLE = {
  open: "pill-open",
  in_progress: "pill-review",
  waiting_on_customer: "pill-pending",
  resolved: "pill-approved",
  closed: "pill-closed",
};

export default function Tickets() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ subject: "", description: "", category: "General", priority: "medium" });
  const [pending, setPending] = useState([]); // File[] queued for upload on submit
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const nav = useNavigate();

  const load = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (q) params.q = q;
      const { data } = await api.get("/tickets", { params });
      setTickets(data);
    } catch { toast.error("Failed to load tickets"); }
  };
  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]); // eslint-disable-line

  const addFiles = (list) => {
    const files = Array.from(list || []);
    const bad = files.find((f) => f.size > MAX_TICKET_FILE_MB * 1024 * 1024);
    if (bad) {
      toast.error(`${bad.name} exceeds ${MAX_TICKET_FILE_MB} MB`);
      return;
    }
    setPending((prev) => [...prev, ...files]);
  };

  const removeFile = (idx) => setPending((prev) => prev.filter((_, i) => i !== idx));

  const uploadPending = async () => {
    const uploaded = [];
    for (const f of pending) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", "ticket_attachment");
      const { data } = await api.post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      uploaded.push({
        id: data.id,
        filename: data.filename,
        content_type: data.content_type,
        size: data.size,
        fingerprint: data.fingerprint,
      });
    }
    return uploaded;
  };

  const create = async () => {
    if (!form.subject.trim() || !form.description.trim()) return toast.error("Subject and description required");
    setSaving(true);
    try {
      const attachments = pending.length ? await uploadPending() : [];
      const { data } = await api.post("/tickets", { ...form, attachments });
      toast.success(`Ticket ${data.ticket_number} created`);
      setCreating(false);
      setForm({ subject: "", description: "", category: "General", priority: "medium" });
      setPending([]);
      nav(`/tickets/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const list = useMemo(() => tickets, [tickets]);

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <SectionHeader
        title="Support Tickets"
        subtitle={user.role === "super_admin"
          ? "PropHR support desk , every tenant ticket in one place."
          : "Reach the PropHR platform team. Track every reply here."}
        right={
          <button className="btn-primary" onClick={() => setCreating(true)} data-testid={TID.ticketCreateBtn}>
            <Plus size={14}/> New ticket
          </button>
        }
      />

      <div className="data-cell p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={2}/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search subject or ticket number…" className="input pl-9"/>
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input !w-auto" data-testid="tickets-status-filter">
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In progress</option>
          <option value="waiting_on_customer">Waiting on customer</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
      </div>

      {list.length === 0 ? (
        <EmptyState
          icon={LifeBuoy}
          title="No tickets yet"
          subtitle="Open your first support ticket , replies land right here."
          action={<button className="btn-primary" onClick={() => setCreating(true)}><Plus size={14}/> New ticket</button>}
        />
      ) : (
        <div className="space-y-2">
          {list.map((t) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className="data-cell p-4 hover:border-blue-600 transition-colors"
              data-testid={`ticket-row-${t.id}`}
            >
              <Link to={`/tickets/${t.id}`} className="block">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-[10px] font-mono text-slate-500">{t.ticket_number}</span>
                      <span className={"pill " + (STATUS_STYLE[t.status] || "pill-open")}>{t.status.replaceAll("_", " ")}</span>
                      <span className={"pill pill-" + t.priority}>{t.priority}</span>
                      <span className="text-[10px] font-mono text-slate-500">{t.category}</span>
                      {t.messages?.some((m) => (m.attachments || []).length) && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono text-slate-500">
                          <Paperclip size={10} strokeWidth={2}/>
                          {t.messages.reduce((n, m) => n + (m.attachments || []).length, 0)}
                        </span>
                      )}
                    </div>
                    <div className="font-semibold text-slate-900">{t.subject}</div>
                    <div className="text-xs text-slate-500 mt-1 line-clamp-1">{t.description}</div>
                    <div className="text-[10px] font-mono text-slate-500 mt-2">
                      {t.reporter_name} · {t.messages.length} message{t.messages.length === 1 ? "" : "s"} · {new Date(t.updated_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {creating && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ y: 8, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 8, opacity: 0 }}
              className="bg-white border border-slate-300 max-w-xl w-full"
              data-testid="ticket-create-modal"
            >
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-display text-xl font-semibold">New support ticket</h3>
                <button onClick={() => { setCreating(false); setPending([]); }}><X size={18}/></button>
              </div>
              <div className="p-5 space-y-3">
                <div>
                  <label className="eyebrow block mb-1">Subject</label>
                  <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="input" data-testid={TID.ticketSubject}/>
                </div>
                <div>
                  <label className="eyebrow block mb-1">Description</label>
                  <textarea rows={5} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input" data-testid={TID.ticketDescription}/>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="eyebrow block mb-1">Category</label>
                    <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input">
                      {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="eyebrow block mb-1">Priority</label>
                    <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="input">
                      {PRIORITIES.map(p => <option key={p}>{p}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="eyebrow block mb-1">Attachments (optional, up to {MAX_TICKET_FILE_MB} MB each)</label>
                  <input
                    ref={fileRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                    data-testid="ticket-file-input"
                  />
                  <button
                    type="button"
                    className="btn-ghost text-xs"
                    onClick={() => fileRef.current?.click()}
                    data-testid="ticket-file-picker"
                  >
                    <Paperclip size={12} strokeWidth={2}/> Add files
                  </button>
                  {pending.length > 0 && (
                    <div className="mt-2 space-y-1" data-testid="ticket-pending-attachments">
                      {pending.map((f, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded px-2 py-1 text-xs">
                          <div className="flex items-center gap-2 min-w-0">
                            <Paperclip size={11} strokeWidth={2} className="shrink-0 text-slate-500"/>
                            <span className="truncate">{f.name}</span>
                            <span className="text-slate-400 font-mono shrink-0">{Math.round(f.size / 1024)} KB</span>
                          </div>
                          <button onClick={() => removeFile(idx)} className="text-slate-500 hover:text-red-700" title="Remove">
                            <Trash2 size={12} strokeWidth={2}/>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
                <button className="btn-ghost" onClick={() => { setCreating(false); setPending([]); }}>Cancel</button>
                <button className="btn-primary" onClick={create} disabled={saving} data-testid={TID.ticketSubmit}>
                  {saving ? "Creating…" : "Open ticket"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
